/**
 * Tab-scoped session persistence for Prompt Builder (draft + AI helper).
 * Cleared on successful publish and on clone entry; not cleared on modal close.
 */

export const BUILDER_SESSION_CACHE_KEY = 'promptHub:builderSession:v1';
export const BUILDER_ASSISTANT_SESSION_CACHE_KEY = 'promptHub:builderAssistantSession:v1';
export const BUILDER_SESSION_VERSION = 1;

const MAX_BYTES = 4 * 1024 * 1024; // stay under typical sessionStorage limits

function safeParse(json) {
  try {
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/** Remove transient assistant placeholders that would flicker after restore. */
export function sanitizeHelperPanelForRestore(helper) {
  if (!helper || typeof helper !== 'object') return null;
  const messages = Array.isArray(helper.messages)
    ? helper.messages.filter((m) => m && !m.isGenerating)
    : [];
  return {
    messages,
    inputValue: typeof helper.inputValue === 'string' ? helper.inputValue : '',
    helperMode: helper.helperMode === 'help' ? 'help' : 'create',
    createFlowStep: helper.createFlowStep ?? null,
    createFlowData:
      helper.createFlowData && typeof helper.createFlowData === 'object'
        ? { ...helper.createFlowData }
        : undefined,
    createFlowGenerating: false,
    helpGenerating: false,
    generatedPrompt: helper.generatedPrompt ?? null,
    createFlowError: null,
    isRunningTest: false,
    lastTestOutput: helper.lastTestOutput ?? null,
    lastTestOutputMessageId: helper.lastTestOutputMessageId ?? null,
    isTweakingOutput: false,
  };
}

/**
 * @param {string|null|undefined} focusPromptId — id of prompt being edited, or null for new draft
 * @param {string|null|undefined} cacheFocusPromptId — stored focus id (same shape)
 */
export function builderSessionCacheMatchesFocus(focusPromptId, cacheFocusPromptId) {
  const a = focusPromptId ?? null;
  const b = cacheFocusPromptId ?? null;
  return a === b;
}

export function readBuilderSessionCache() {
  if (typeof sessionStorage === 'undefined') return null;
  const raw = sessionStorage.getItem(BUILDER_SESSION_CACHE_KEY);
  if (!raw) return null;
  const data = safeParse(raw);
  if (!data || data.version !== BUILDER_SESSION_VERSION) return null;
  if (data.helperPanel) {
    data.helperPanel = sanitizeHelperPanelForRestore(data.helperPanel);
  }
  if (data.builderAssistant) {
    data.builderAssistant = sanitizeBuilderAssistantForRestore(data.builderAssistant);
  }
  return data;
}

export function clearBuilderSessionCache() {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(BUILDER_SESSION_CACHE_KEY);
  } catch {
    /* ignore */
  }
}

/**
 * @param {{ focusPromptId: string|null, promptData: object, helperPanel: object, builderAssistant?: object }} payload
 */
export function writeBuilderSessionCache(payload) {
  if (typeof sessionStorage === 'undefined') return;
  const blob = {
    version: BUILDER_SESSION_VERSION,
    focusPromptId: payload.focusPromptId ?? null,
    promptData: payload.promptData || {},
    helperPanel: sanitizeHelperPanelForRestore(payload.helperPanel) || {
      messages: [],
      inputValue: '',
      helperMode: 'create',
      createFlowStep: null,
      createFlowData: {},
      generatedPrompt: null,
    },
    builderAssistant: sanitizeBuilderAssistantForRestore(payload.builderAssistant),
  };
  try {
    const str = JSON.stringify(blob);
    if (str.length > MAX_BYTES) return;
    sessionStorage.setItem(BUILDER_SESSION_CACHE_KEY, str);
  } catch {
    /* quota or disabled */
  }
}

export function readBuilderAssistantSessionCache() {
  if (typeof sessionStorage === 'undefined') return null;
  try {
    const raw = sessionStorage.getItem(BUILDER_ASSISTANT_SESSION_CACHE_KEY);
    const snapshot = raw ? safeParse(raw) : null;
    return sanitizeBuilderAssistantForRestore(snapshot);
  } catch {
    return null;
  }
}

export function writeBuilderAssistantSessionCache(snapshot) {
  if (typeof sessionStorage === 'undefined') return;
  try {
    const sanitized = sanitizeBuilderAssistantForRestore(snapshot);
    if (!sanitized) {
      sessionStorage.removeItem(BUILDER_ASSISTANT_SESSION_CACHE_KEY);
      return;
    }
    if (!builderAssistantSnapshotHasContent(sanitized)) return;
    const str = JSON.stringify(sanitized);
    if (str.length > MAX_BYTES) return;
    sessionStorage.setItem(BUILDER_ASSISTANT_SESSION_CACHE_KEY, str);
  } catch {
    /* quota or disabled */
  }
}

export function clearBuilderAssistantSessionCache() {
  if (typeof sessionStorage === 'undefined') return;
  try {
    sessionStorage.removeItem(BUILDER_ASSISTANT_SESSION_CACHE_KEY);
  } catch {
    /* ignore */
  }
}

export function builderAssistantSnapshotHasContent(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return false;
  if (snapshot.landingDismissed || snapshot.builderSessionActive) return true;
  const executionSession = snapshot.executionSession;
  if (!executionSession || typeof executionSession !== 'object') return false;
  if (typeof executionSession.composerInput === 'string' && executionSession.composerInput.trim()) return true;
  const testChats = executionSession.testChats;
  if (!testChats || typeof testChats !== 'object') return false;
  return Object.values(testChats).some((messages) => Array.isArray(messages) && messages.length > 0);
}

export function chooseBuilderAssistantSnapshot(...snapshots) {
  const sanitized = snapshots
    .map((snapshot) => sanitizeBuilderAssistantForRestore(snapshot))
    .filter(Boolean);
  return sanitized.find(builderAssistantSnapshotHasContent) || sanitized[0] || null;
}

export function sanitizeBuilderAssistantForRestore(snapshot) {
  if (!snapshot || typeof snapshot !== 'object') return null;
  const executionSession = snapshot.executionSession && typeof snapshot.executionSession === 'object'
    ? {
      ...snapshot.executionSession,
      testChats: sanitizeTestChats(snapshot.executionSession.testChats),
      copySuccess: snapshot.executionSession.copySuccess || {},
      creatorUpdatesDone: Boolean(snapshot.executionSession.creatorUpdatesDone),
      introSuppressedOnce: Boolean(snapshot.executionSession.introSuppressedOnce),
      composerInput: typeof snapshot.executionSession.composerInput === 'string'
        ? snapshot.executionSession.composerInput
        : '',
    }
    : null;

  return {
    landingDismissed: Boolean(snapshot.landingDismissed),
    builderSessionActive: false,
    builderStatusLabel: '',
    assistantModel: typeof snapshot.assistantModel === 'string' ? snapshot.assistantModel : null,
    executionSession,
  };
}

function sanitizeTestChats(testChats) {
  if (!testChats || typeof testChats !== 'object') return {};
  return Object.fromEntries(
    Object.entries(testChats).map(([model, messages]) => [
      model,
      Array.isArray(messages)
        ? messages.filter((message) => message && !message.isGenerating)
        : [],
    ])
  );
}
